import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const inputCls =
    "w-full border-0 border-b border-ink/20 bg-transparent px-0 py-3 text-sm font-light text-ink placeholder:text-ink/35 focus:border-gold focus:outline-none focus:ring-0 transition-colors duration-300";
const labelCls = "block text-[10px] font-medium uppercase tracking-[0.25em] text-ink/50";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const Field = ({ label, error, children }) => (
    <div>
        <label className={labelCls}>{label}</label>
        <div className="mt-1">{children}</div>
        {error && <p className="mt-1 text-xs text-red-700" data-testid="form-field-error">{error}</p>}
    </div>
);

const useEnquiryForm = (type, initial, required, extra = {}) => {
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        const errs = {};
        required.forEach((k) => {
            if (!String(form[k] || "").trim()) errs[k] = "Required";
        });
        if (form.email && !EMAIL_RE.test(form.email)) errs.email = "Enter a valid email";
        setErrors(errs);
        if (Object.keys(errs).length) return;
        setSubmitting(true);
        try {
            await api.post("/enquiries", { type, data: { ...extra, ...form } });
            toast.success("Thank you. The LUXATI team will be in touch shortly.");
            setForm(initial);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return { form, set, errors, submitting, submit };
};

export const BuyerForm = ({ testid = "buyer-form", dark = false, extra = {} }) => {
    const { form, set, errors, submitting, submit } = useEnquiryForm(
        "buyer",
        { name: "", phone: "", email: "", market: "", budget: "", property_type: "", purpose: "", timeline: "", message: "" },
        ["name", "phone", "email"],
        extra
    );
    return (
        <form data-testid={testid} onSubmit={submit} className="grid gap-7 sm:grid-cols-2" noValidate>
            <Field label="Name *" error={errors.name}>
                <input data-testid={`${testid}-name`} className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" />
            </Field>
            <Field label="Phone *" error={errors.phone}>
                <input data-testid={`${testid}-phone`} className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91" />
            </Field>
            <Field label="Email *" error={errors.email}>
                <input data-testid={`${testid}-email`} type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </Field>
            <Field label="Preferred Market">
                <select data-testid={`${testid}-market`} className={inputCls} value={form.market} onChange={set("market")}>
                    <option value="">Select</option>
                    <option>Delhi NCR / India</option>
                    <option>Dubai</option>
                    <option>Other / International</option>
                </select>
            </Field>
            <Field label="Budget">
                <select data-testid={`${testid}-budget`} className={inputCls} value={form.budget} onChange={set("budget")}>
                    <option value="">Select</option>
                    <option>Under ₹2 Cr</option>
                    <option>₹2 – 5 Cr</option>
                    <option>₹5 – 10 Cr</option>
                    <option>₹10 Cr+</option>
                    <option>USD-denominated (Dubai / Global)</option>
                </select>
            </Field>
            <Field label="Property Type">
                <select data-testid={`${testid}-property-type`} className={inputCls} value={form.property_type} onChange={set("property_type")}>
                    <option value="">Select</option>
                    <option>Apartment</option>
                    <option>Villa / Independent Floor</option>
                    <option>Plot</option>
                    <option>Commercial</option>
                    <option>Open to Suggestions</option>
                </select>
            </Field>
            <Field label="Purpose">
                <select data-testid={`${testid}-purpose`} className={inputCls} value={form.purpose} onChange={set("purpose")}>
                    <option value="">Select</option>
                    <option>Investment</option>
                    <option>End Use</option>
                </select>
            </Field>
            <Field label="Expected Timeline">
                <select data-testid={`${testid}-timeline`} className={inputCls} value={form.timeline} onChange={set("timeline")}>
                    <option value="">Select</option>
                    <option>0 – 3 months</option>
                    <option>3 – 6 months</option>
                    <option>6 – 12 months</option>
                    <option>Exploring</option>
                </select>
            </Field>
            <div className="sm:col-span-2">
                <Field label="Message">
                    <textarea data-testid={`${testid}-message`} rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us what you are looking for" />
                </Field>
            </div>
            <div className="sm:col-span-2">
                <button
                    data-testid={`${testid}-submit`}
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex items-center gap-3 bg-ink px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-ink disabled:opacity-50"
                >
                    {submitting ? "Sending…" : "Request a Consultation"}
                </button>
            </div>
        </form>
    );
};

export const PartnerForm = ({ testid = "partner-form" }) => {
    const { form, set, errors, submitting, submit } = useEnquiryForm(
        "partner",
        { name: "", company: "", designation: "", phone: "", email: "", market: "", message: "" },
        ["name", "company", "phone", "email"]
    );
    return (
        <form data-testid={testid} onSubmit={submit} className="grid gap-7 sm:grid-cols-2" noValidate>
            <Field label="Name *" error={errors.name}>
                <input data-testid={`${testid}-name`} className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" />
            </Field>
            <Field label="Company *" error={errors.company}>
                <input data-testid={`${testid}-company`} className={inputCls} value={form.company} onChange={set("company")} placeholder="Organisation" />
            </Field>
            <Field label="Designation">
                <input data-testid={`${testid}-designation`} className={inputCls} value={form.designation} onChange={set("designation")} placeholder="Role" />
            </Field>
            <Field label="Phone *" error={errors.phone}>
                <input data-testid={`${testid}-phone`} className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91" />
            </Field>
            <Field label="Email *" error={errors.email}>
                <input data-testid={`${testid}-email`} type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
            </Field>
            <Field label="Market">
                <select data-testid={`${testid}-market`} className={inputCls} value={form.market} onChange={set("market")}>
                    <option value="">Select</option>
                    <option>Delhi NCR / India</option>
                    <option>Dubai</option>
                    <option>Other / International</option>
                </select>
            </Field>
            <div className="sm:col-span-2">
                <Field label="Message">
                    <textarea data-testid={`${testid}-message`} rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about your project or partnership interest" />
                </Field>
            </div>
            <div className="sm:col-span-2">
                <button
                    data-testid={`${testid}-submit`}
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex items-center gap-3 bg-ink px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-ink disabled:opacity-50"
                >
                    {submitting ? "Sending…" : "Partner With LUXATI"}
                </button>
            </div>
        </form>
    );
};

export const GeneralForm = ({ testid = "general-form" }) => {
    const { form, set, errors, submitting, submit } = useEnquiryForm(
        "general",
        { name: "", email: "", phone: "", message: "" },
        ["name", "email", "message"]
    );
    return (
        <form data-testid={testid} onSubmit={submit} className="grid gap-7" noValidate>
            <Field label="Name *" error={errors.name}>
                <input data-testid={`${testid}-name`} className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" />
            </Field>
            <Field label="Email *" error={errors.email}>
                <input data-testid={`${testid}-email`} type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </Field>
            <Field label="Phone">
                <input data-testid={`${testid}-phone`} className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91" />
            </Field>
            <Field label="Message *" error={errors.message}>
                <textarea data-testid={`${testid}-message`} rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="How can we help?" />
            </Field>
            <div>
                <button
                    data-testid={`${testid}-submit`}
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-3 bg-ink px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-ink disabled:opacity-50"
                >
                    {submitting ? "Sending…" : "Send Enquiry"}
                </button>
            </div>
        </form>
    );
};

export const DemoForm = ({ testid = "demo-form" }) => {
    const { form, set, errors, submitting, submit } = useEnquiryForm(
        "demo",
        { name: "", company: "", email: "", phone: "", business_type: "", looking_for: "", message: "" },
        ["name", "company", "email", "phone", "business_type"]
    );
    return (
        <form data-testid={testid} onSubmit={submit} className="grid gap-7 sm:grid-cols-2" noValidate>
            <Field label="Full Name *" error={errors.name}>
                <input data-testid={`${testid}-name`} className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" />
            </Field>
            <Field label="Company / Organisation *" error={errors.company}>
                <input data-testid={`${testid}-company`} className={inputCls} value={form.company} onChange={set("company")} placeholder="Organisation" />
            </Field>
            <Field label="Email *" error={errors.email}>
                <input data-testid={`${testid}-email`} type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
            </Field>
            <Field label="Phone *" error={errors.phone}>
                <input data-testid={`${testid}-phone`} className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91" />
            </Field>
            <Field label="Business Type *" error={errors.business_type}>
                <select data-testid={`${testid}-business-type`} className={inputCls} value={form.business_type} onChange={set("business_type")}>
                    <option value="">Select</option>
                    <option>Developer</option>
                    <option>Brokerage / Channel Partner</option>
                    <option>Real Estate Advisory</option>
                    <option>Other</option>
                </select>
            </Field>
            <Field label="What Are You Looking For?">
                <select data-testid={`${testid}-looking-for`} className={inputCls} value={form.looking_for} onChange={set("looking_for")}>
                    <option value="">Select</option>
                    <option>Platform</option>
                    <option>White Label Platform</option>
                    <option>Both</option>
                    <option>General Enquiry</option>
                </select>
            </Field>
            <div className="sm:col-span-2">
                <Field label="Message">
                    <textarea data-testid={`${testid}-message`} rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us briefly about your requirements" />
                </Field>
            </div>
            <div className="sm:col-span-2">
                <button
                    data-testid={`${testid}-submit`}
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-3 bg-ink px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-ink disabled:opacity-50"
                >
                    {submitting ? "Sending…" : "Request a Private Demo"}
                </button>
            </div>
        </form>
    );
};

export const ProjectEnquiryForm = ({ testid = "project-enquiry-form", context = {} }) => {
    const { form, set, errors, submitting, submit } = useEnquiryForm(
        "buyer",
        { name: "", email: "", phone: "", budget: "", purpose: "", timeline: "", message: "" },
        ["name", "email", "phone"],
        context
    );
    return (
        <form data-testid={testid} onSubmit={submit} className="grid gap-7 sm:grid-cols-2" noValidate>
            <Field label="Full Name *" error={errors.name}>
                <input data-testid={`${testid}-name`} className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" />
            </Field>
            <Field label="Email *" error={errors.email}>
                <input data-testid={`${testid}-email`} type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </Field>
            <Field label="Phone *" error={errors.phone}>
                <input data-testid={`${testid}-phone`} className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91" />
            </Field>
            <Field label="Budget">
                <select data-testid={`${testid}-budget`} className={inputCls} value={form.budget} onChange={set("budget")}>
                    <option value="">Select</option>
                    <option>Under ₹2 Cr</option>
                    <option>₹2 – 5 Cr</option>
                    <option>₹5 – 10 Cr</option>
                    <option>₹10 Cr+</option>
                </select>
            </Field>
            <Field label="Purpose">
                <select data-testid={`${testid}-purpose`} className={inputCls} value={form.purpose} onChange={set("purpose")}>
                    <option value="">Select</option>
                    <option>Investment</option>
                    <option>End Use</option>
                </select>
            </Field>
            <Field label="Expected Timeline">
                <select data-testid={`${testid}-timeline`} className={inputCls} value={form.timeline} onChange={set("timeline")}>
                    <option value="">Select</option>
                    <option>0 – 3 months</option>
                    <option>3 – 6 months</option>
                    <option>6 – 12 months</option>
                    <option>Exploring</option>
                </select>
            </Field>
            <div className="sm:col-span-2">
                <Field label="Message">
                    <textarea data-testid={`${testid}-message`} rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Anything specific you'd like to know?" />
                </Field>
            </div>
            <div className="sm:col-span-2">
                <button
                    data-testid={`${testid}-submit`}
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-3 bg-ink px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-ink disabled:opacity-50"
                >
                    {submitting ? "Sending…" : "Request a Consultation"}
                </button>
            </div>
        </form>
    );
};
